import { useDraggable } from "@dnd-kit/core";
import type { FormFieldKind } from "@rwaforms/shared";
import { useBuilderStore } from "../hooks/useBuilderStore";

const FIELD_GROUPS: Array<{
  title: string;
  items: Array<{ kind: FormFieldKind; label: string; description: string }>;
}> = [
    {
      title: "Input",
      items: [
        { kind: "short_text", label: "Short answer", description: "Collect brief text responses." },
        { kind: "long_text", label: "Paragraph", description: "Allow detailed feedback." },
        { kind: "email", label: "Email", description: "Collect email addresses with validation." },
        { kind: "phone", label: "Phone number", description: "Capture phone numbers with formatting." },
        { kind: "name", label: "Name", description: "Collect first and last names." },
        { kind: "number", label: "Number", description: "Capture numeric input with validation." },
        { kind: "date", label: "Date", description: "Pick dates with optional ranges." },
        { kind: "time", label: "Time", description: "Collect time inputs for scheduling." },
        { kind: "address", label: "Address", description: "Capture complete Indian address with all fields." },
      ],
    },
    {
      title: "Choices",
      items: [
        { kind: "multiple_choice", label: "Single choice", description: "Select one option." },
        { kind: "checkboxes", label: "Multiple choice", description: "Select multiple options." },
        { kind: "dropdown", label: "Dropdown", description: "Choose from a list or synced data." },
        { kind: "rating", label: "Rating", description: "Star or scale based scoring." },
      ],
    },
    {
      title: "Advanced",
      items: [
        { kind: "file_upload", label: "File upload", description: "Collect documents or media." },
        { kind: "calculated", label: "Calculated field", description: "Derive values from responses." },
        { kind: "static_text", label: "Content block", description: "Explain or segment the form." },
      ],
    },
  ];

function PaletteItem({
  kind,
  label,
  description,
  onQuickAdd,
}: {
  kind: FormFieldKind;
  label: string;
  description: string;
  onQuickAdd(): void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${kind}`,
    data: {
      type: "palette-field",
      fieldKind: kind,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className="surface"
      style={{
        padding: "0.9rem",
        borderRadius: "12px",
        border: "1px solid var(--color-border)",
        transition: "background 0.2s ease, border 0.2s ease",
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: "0.5rem" }}>
        {/* Draggable area - only the label/description */}
        <div
          {...attributes}
          {...listeners}
          style={{ flex: 1, cursor: "grab", touchAction: "none" }}
        >
          <h4 style={{ margin: 0, fontSize: "0.95rem" }}>{label}</h4>
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
            {description}
          </p>
        </div>
        {/* Add button - NOT inside drag area */}
        <button
          type="button"
          className="button ghost"
          style={{
            padding: "0.4rem 0.75rem",
            fontSize: "0.8rem",
            flexShrink: 0,
            touchAction: "manipulation",
          }}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onQuickAdd();
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function FieldPalette(): JSX.Element {
  const addField = useBuilderStore((state) => state.addField);
  const sectionId = useBuilderStore((state) => state.selectedSectionId);

  return (
    <aside className="surface scroll-container" style={{ padding: "1.5rem" }}>
      <header style={{ marginBottom: "1.25rem" }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "0.35rem" }}>Add fields</h2>
        <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          Drag blocks onto the canvas or tap Add to insert in the current section.
        </p>
      </header>
      <div style={{ display: "grid", gap: "1.25rem" }}>
        {FIELD_GROUPS.map((group) => (
          <section key={group.title}>
            <h3 className="section-title">{group.title}</h3>
            <div style={{ display: "grid", gap: "0.8rem" }}>
              {group.items.map((item) => (
                <PaletteItem
                  key={item.kind}
                  kind={item.kind}
                  label={item.label}
                  description={item.description}
                  onQuickAdd={() => addField(item.kind, sectionId ?? null)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </aside>
  );
}

export default FieldPalette;
